import qs from "qs"
import { Fragment, useEffect, useState } from "react"
import { LoaderCircleIcon, PlusIcon, TrashIcon } from "lucide-react"
import { PopoverClose } from "@radix-ui/react-popover"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData } from "react-router"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

import { FiltersWrapper, type StoredSession } from "@/components/Filters"

import { fetchAPI, handleResponse, throwIfNotOk, reactHookFormSubmitShim } from "@/utils"
import { getOrganisations } from "@/utils/projects"

import { hasPermission } from "@/machines/Authmachine"

import type { RootLoader } from "@/routes/root"
import { FieldInfoComponent, type FieldDetail } from "@/components/FieldInfo"
import { AuditUserExclusionCreateSchema, AuditUserExclusionSchema } from "@/schemas/viewer/audit-user-exclusion"
import { ValidationSummary } from "@/components/ui/ValidationSummary"
import { formatFluentValidationErrors, formatRHFErrors, type FluentValidationError } from "@/utils/forms"

const AUDIT_USER_EXCLUSIONS_FIELDS = {
  USERNAME: "userName",
} as const

const routeId = "exclusions-builder"
const sessionKey = "exclusion-filter-storage"

type ActionReturn = {
  action: "CREATE" | "DELETE_ALL"
  errorMessage?: string
  errors: Record<string, string[]>
}

type MainActionReturn = {
  action: "DELETE"
  errorMessage?: string
  errors: Record<string, string[]>
}

type AddUserExclusionFormData = {
  action: "CREATE"
  organisation: string
  userName: string
}

function buildValidationSummaryErrors(
  serverData?: { errorMessage?: string; errors?: Record<string, string[]> },
  clientErrors?: Record<string, string[]>,
) {
  return {
    ...(clientErrors ?? {}),
    ...(serverData?.errors ?? {}),
    ...(serverData?.errorMessage != null ? { general: [serverData.errorMessage] } : {}),
  }
}

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  try {
    const users = await fetchAPI<string[]>(`/api/UnityViewer/audituserexclusion/${loadedOrganisation}`).then(
      handleResponse,
    )

    const fieldInfoArray = await fetchAPI<FieldDetail[]>(`/api/sections/Unity Viewer/Audit User Exclusions`)
      .then(handleResponse)
      .catch(() => [])

    return { users, fieldInfoArray, loadedOrganisation }
  } catch (error) {
    if (error != null && typeof error === "object" && "status" in error && error.status === 404) {
      return { users: [], loadedOrganisation }
    }
    throw error
  }
}

async function action({ request }: { request: Request }): Promise<ActionReturn> {
  const { data, success, error } = AuditUserExclusionSchema.safeParse(qs.parse(await request.text()))

  if (!success || data.action === "DELETE") {
    return {
      action: "CREATE",
      errorMessage: "Failed to validate form data.",
      errors: error?.flatten().fieldErrors ?? {},
    }
  }

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  switch (data.action) {
    case "DELETE_ALL":
      try {
        await fetchAPI(`/api/UnityViewer/audituserexclusion/${data.organisation}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
      } catch (error) {
        const response = error as Response
        if (response.status === 400) {
          const errorData = (await response.json()) as { errors: Record<string, string[]> }
          errors = errorData.errors
        } else {
          console.error(error)
          errorMessage = "Error deleting all exclusions"
        }
      }
      break
    case "CREATE": {
      try {
        await fetchAPI(`/api/UnityViewer/audituserexclusion/${data.organisation}/${data.userName}`, {
          method: "POST",
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response.status === 304) {
          errors = { userName: ["This user is already excluded for the selected organisation."] }
        } else if (response.status === 400) {
          const errorData = (await response.json()) as unknown
          if (Array.isArray(errorData)) {
            errors = formatFluentValidationErrors(errorData as FluentValidationError[])
          } else {
            errors = (errorData as { errors: Record<string, string[]> }).errors
          }
        } else {
          console.error(error)
          errorMessage = "Error excluding user"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

async function mainAction({ request }: { request: Request }): Promise<MainActionReturn> {
  const formData = await request.formData()
  const parsed = AuditUserExclusionSchema.safeParse({
    action: formData.get("action"),
    organisation: formData.get("organisation"),
    userName: formData.get("userName"),
  })

  if (!parsed.success || parsed.data.action !== "DELETE") {
    return {
      action: "DELETE",
      errorMessage: "Failed to validate form data.",
      errors: parsed.success ? {} : parsed.error.flatten().fieldErrors,
    }
  }

  const data = parsed.data

  let errorMessage: string | undefined
  let errors: Record<string, string[]> = {}

  switch (data.action) {
    case "DELETE": {
      try {
        await fetchAPI(`/api/UnityViewer/audituserexclusion/${data.organisation}/${data.userName}`, {
          method: "DELETE",
        }).then(throwIfNotOk)
      } catch (error) {
        const response = error as Response
        if (response.status === 400) {
          const errorData = (await response.json()) as { errors: Record<string, string[]> }
          errors = errorData.errors
        } else {
          console.error(error)
          errorMessage = "Error deleting user exclusion"
        }
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action: data.action, errorMessage, errors }
}

export function UserExclusions() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  return (
    <Fragment>
      <div className="flex items-center gap-4">
        {rootData?.organisations != null && rootData.organisations.length > 0 && (
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data?.loadedOrganisation ?? rootData.organisations[0] ?? ""}
              items={rootData.organisations}
            />
          </FiltersWrapper>
        )}
        <div className="flex-1" />
        <ExclusionsAddNew />
        <ExclusionsDeleteAll />
      </div>
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const fetcher = useFetcher<MainActionReturn>()
  const data = useLoaderData<typeof loader>()

  const validationSummaryErrors = buildValidationSummaryErrors(fetcher.data)

  return (
    <div className="h-screen overflow-y-auto">
      <ValidationSummary errors={validationSummaryErrors} />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead />
            <TableHead className="text-left">User Name</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.users.length > 0 ? (
            data.users.map((userName, index) => (
              <TableRow key={userName} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell>{userName}</TableCell>
                <TableCell>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label="Delete"
                        className="data-[state=open]:bg-accent data-[state=open]:text-accent-foreground"
                      >
                        <TrashIcon />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent side="right" align="start" className="max-w-sm">
                      <p className="text-sm text-pretty">
                        &quot;<span className="font-semibold">{userName}</span>&quot; will be deleted from the
                        exclusions. They will be visible within the audit trail afterwards. Are you sure?
                      </p>
                      <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
                        <input type="hidden" name="action" value="DELETE" />
                        <input type="hidden" name="organisation" value={data.loadedOrganisation} />
                        <input type="hidden" name="userName" value={userName} />
                        {fetcher.state !== "idle" && fetcher.formData?.get("userName") === userName && (
                          <span className="flex items-center justify-start gap-1 pr-4">
                            <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
                          </span>
                        )}
                        <div className="flex-1" />
                        <PopoverClose asChild>
                          <Button>Cancel</Button>
                        </PopoverClose>
                        <Button type="submit" variant="ghost">
                          Confirm
                        </Button>
                      </fetcher.Form>
                    </PopoverContent>
                  </Popover>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} className="py-4 text-center text-gray-500">
                No data found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function ExclusionsDeleteAll() {
  const [open, setOpen] = useState(false)

  const fetcher = useFetcher<ActionReturn>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.data?.action !== "DELETE_ALL" || fetcher.data.errorMessage != null) return
    setOpen(false)
  }, [fetcher.data])

  if (!hasPermission("deleteAccess") || data?.users == null || data.users.length === 0) return null

  const validationSummaryErrors = buildValidationSummaryErrors(fetcher.data)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="destructive">
          <TrashIcon /> Delete All
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <ValidationSummary errors={validationSummaryErrors} />
        <p className="text-sm text-pretty">
          Are you sure you want to <span className="font-semibold">DELETE ALL</span> exclusions in the &quot;
          <span className="font-semibold">{data.loadedOrganisation}</span>&quot; organisation?
        </p>
        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
          <input type="hidden" name="action" value="DELETE_ALL" />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          {fetcher.state !== "idle" && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button>Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="ghost">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function ExclusionsAddNew() {
  const [open, setOpen] = useState(false)
  const [createResult, setCreateResult] = useState<ActionReturn | undefined>(undefined)

  const fetcher = useFetcher<ActionReturn>()

  const data = useRouteLoaderData<typeof loader>(routeId)
  const rootData = useRouteLoaderData<RootLoader>("root")

  const getCurrentOrganisation = () => {
    if (data?.loadedOrganisation != null && data.loadedOrganisation !== "") return data.loadedOrganisation

    const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>
    return filters.parsed?.organisation ?? rootData?.organisations[0] ?? ""
  }

  const currentOrganisation = getCurrentOrganisation()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddUserExclusionFormData>({
    resolver: zodResolver(AuditUserExclusionCreateSchema),
    defaultValues: {
      action: "CREATE",
      organisation: currentOrganisation,
      userName: "",
    },
  })

  useEffect(() => {
    if (fetcher.data?.action !== "CREATE") return
    setCreateResult(fetcher.data)
  }, [fetcher.data])

  useEffect(() => {
    if (createResult?.action !== "CREATE" || createResult.errorMessage != null) return
    if (Object.keys(createResult.errors).length > 0) return
    reset({ action: "CREATE", organisation: currentOrganisation, userName: "" })
    setOpen(false)
  }, [createResult, currentOrganisation, reset])

  useEffect(() => {
    if (!open) {
      reset({ action: "CREATE", organisation: currentOrganisation, userName: "" })
      setCreateResult(undefined)
    }
  }, [currentOrganisation, open, reset])

  const combinedErrors = buildValidationSummaryErrors(createResult, formatRHFErrors(errors))

  if (!hasPermission("writeAccess") || currentOrganisation == null || currentOrganisation === "") return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New User Exclusion</DialogTitle>
          <DialogDescription>
            Excluding a user from the &quot;{currentOrganisation}&quot; organisation.
          </DialogDescription>
        </DialogHeader>
        <form
          method="POST"
          className="space-y-4"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit((data) => reactHookFormSubmitShim(data, fetcher.submit))}
        >
          <input type="hidden" {...register("action")} value="CREATE" />
          <input type="hidden" {...register("organisation")} value={currentOrganisation} />
          <ValidationSummary errors={combinedErrors} />
          <div className="flex-1" />
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label className="text-sm font-medium">User Name</label>
              <FieldInfoComponent
                name={AUDIT_USER_EXCLUSIONS_FIELDS.USERNAME}
                fieldDetails={data?.fieldInfoArray ?? []}
              />
            </div>
            <Input required type="text" placeholder="User Name" {...register("userName")} />
          </div>
          <div className="flex items-center justify-start gap-4">
            {fetcher.state !== "idle" ? (
              <span className="flex items-center justify-start gap-1 pr-4">
                <LoaderCircleIcon className="size-4 animate-spin" /> Loading
              </span>
            ) : null}
            <div className="flex-1" />
            <DialogClose asChild>
              <Button
                variant="ghost"
                onClick={() => {
                  reset({ action: "CREATE", organisation: currentOrganisation, userName: "" })
                  setCreateResult(undefined)
                }}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Submit</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

UserExclusions.Main = Main
UserExclusions.loader = loader
UserExclusions.action = action
UserExclusions.routeId = routeId

Main.action = mainAction
